import { stripArrayRowIds } from 'features/libra/fieldValues/arrayRowIds'
import {
  isEmptyFieldValue,
  unwrapLlmValue,
  withFieldOrigin,
} from 'features/libra/ontology/ontologyFieldDefaults'
import {
  FieldOrigin,
  FieldValue,
  FieldValues,
} from 'features/libra/ontology/ontologyFieldTypes'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import omit from 'lodash/omit'
import type { SetStateAction } from 'react'

const COMPONENTS_KEY = 'components'
const ORIGIN_KEYS = ['updateSource', 'updatedAt'] as const

export type FieldOriginOption = {
  /**
   * Origin per written key. Keys without an origin are left as-is (recorded as
   * `human` at save).
   */
  origin?: Partial<Record<string, FieldOrigin>>
}

export type FieldValuesSetter = (
  action: SetStateAction<FieldValues>,
  option?: FieldOriginOption
) => void

/**
 * Applies a setter's origin option to exactly the listed keys present in
 * `values`, overwriting any existing mark. Keeping this in the setters means
 * components never mint marks.
 */
export function applyOriginOption(
  values: FieldValues,
  option?: FieldOriginOption
): FieldValues {
  const origin = option?.origin
  if (!origin) return values
  return Object.fromEntries(
    Object.entries(values).map(([key, wrapper]) => {
      const resolved = key === COMPONENTS_KEY ? undefined : origin[key]
      return [
        key,
        wrapper && resolved ? withFieldOrigin(wrapper, resolved) : wrapper,
      ]
    })
  )
}

/** Compares two wrappers' values ignoring client-minted array row ids. */
function haveSameValue(a: FieldValue, b: FieldValue): boolean {
  return isEqual(stripArrayRowIds(a.value), stripArrayRowIds(b.value))
}

/**
 * Whether a legacy wrapper (no recorded origin) was AI-authored: either its
 * citation says so, or its value still equals the AI's output. Legacy arrays
 * carry row ids only on `value`, so rows are compared without them.
 */
export function isLegacyAiFilled(wrapper: FieldValue): boolean {
  if (wrapper.source?.value.startsWith('AI -')) return true
  if (wrapper.llmValue === undefined || wrapper.llmValue === null) return false
  return isEqual(
    stripArrayRowIds(wrapper.value),
    stripArrayRowIds(unwrapLlmValue(wrapper.llmValue))
  )
}

/**
 * Origin for a wrapper copied as-is from another ticket location: keeps a valid
 * recorded origin, otherwise infers `llm` for legacy AI content and `system`
 * for anything else, so a copy never turns AI-authored content into `human`.
 */
export function inheritCopiedOrigin(wrapper: FieldValue): FieldValue {
  if (wrapper.updateSource) return wrapper
  return withFieldOrigin(
    omit(wrapper, ORIGIN_KEYS),
    isLegacyAiFilled(wrapper) ? 'llm' : 'system'
  )
}

/** Drops the origin marks so dirty checks only see real content changes. */
export function omitOrigin(fieldValues: FieldValues): FieldValues {
  return Object.fromEntries(
    Object.entries(fieldValues).map(([fieldId, wrapper]) => [
      fieldId,
      omit(wrapper, ORIGIN_KEYS),
    ])
  )
}

/**
 * The `aiExtracted` flag for a changed wrapper: an explicit not-found is kept,
 * a persisted or incoming flag is cleared to the `null` sentinel (an omitted
 * key would let the old one stick through the merge; the save step removes it
 * before persisting), and a field that never had one stays without.
 */
function nextAiExtracted(
  wrapper: FieldValue,
  previous: FieldValue | undefined
): Partial<FieldValue> {
  if (wrapper.aiExtracted === false) return { aiExtracted: false }
  return !isNil(wrapper.aiExtracted) || !isNil(previous?.aiExtracted)
    ? { aiExtracted: null }
    : {}
}

/**
 * Stamps who changed each incoming field, comparing against what is persisted
 * (`existing`). `mergeFieldValues` is `{...prev, ...next}`, so every flag that
 * must change is written explicitly here -- an omitted key would let the old
 * one stick.
 *
 * - Unchanged value (semantically, ignoring array row ids): the persisted
 *   origin and row ids are kept, so an AI run that confirms a human value
 *   leaves it `human`.
 * - Changed value: `updateSource` is the incoming mark, defaulting to `human`
 *   (an unmarked wrapper is a manual edit), and `updatedAt` is the current time.
 * - A persisted `aiExtracted` flag is cleared to `null` on any change or
 *   successful AI result, except an explicit not-found (`false`), which is kept.
 *   `null` is a pre-merge sentinel, removed before persisting so the PATCH
 *   deletes the key.
 */
export function resolveFieldOriginsForSave(
  existing: FieldValues,
  incoming: FieldValues
): FieldValues {
  const now = new Date().toISOString()
  return Object.fromEntries(
    Object.entries(incoming).map(([fieldId, wrapper]) => {
      if (fieldId === COMPONENTS_KEY) return [fieldId, wrapper]
      const previous = existing[fieldId]
      const isAiSuccess =
        wrapper.updateSource === 'llm' &&
        !isEmptyFieldValue(wrapper.value) &&
        wrapper.aiExtracted !== false

      if (previous && haveSameValue(previous, wrapper)) {
        const kept: FieldValue = {
          ...omit(wrapper, ORIGIN_KEYS),
          value: previous.value,
          ...(previous.updateSource && { updateSource: previous.updateSource }),
          ...(previous.updatedAt && { updatedAt: previous.updatedAt }),
        }
        return [
          fieldId,
          isAiSuccess && !isNil(previous.aiExtracted)
            ? { ...kept, aiExtracted: null }
            : kept,
        ]
      }

      return [
        fieldId,
        {
          ...wrapper,
          updateSource: wrapper.updateSource ?? 'human',
          updatedAt: now,
          ...nextAiExtracted(wrapper, previous),
        },
      ]
    })
  )
}
